// 창고(해외창고) 엑셀 → 정규화 행/그룹 변환. 순수 함수 모듈.
// 출고리스트(출고수량)와 재고리스트(재고수량)는 0~31번 컬럼 구조가 동일해서 한 파서로 처리한다.
//
// 이 파일을 별도로 둔 이유 = 창고가 만드는 엑셀은 헤더를 믿을 수 없기 때문이다.
//   · '입고센터'(14) 칸에는 실제로 박스규격(1号大箱)이 들어온다
//   · '입고예정일'(13) 칸에는 날짜 대신 중국어 메모("出库要拿出猫薄荷")가 오기도 한다
//   · 'sku id'(8) 칸은 창고 자유메모다("350出库350保管", "不良28个")
//   · 32번 이후는 창고 내부용이라 헤더와 값이 어긋난다(保管开始时间 자리에 未分配组长)
// 그래서 0~31번만 읽고, 값은 패턴으로 검증한다.

// 컬럼 인덱스 (두 양식 공통)
export const COL = {
  주문날짜: 0, 송장번호: 1, 상품URL: 2, 이미지URL: 3,
  옵션: 5, 바코드: 6, 상품명: 7, 창고메모: 8,
  주문수량: 9, 수량: 10, 업체: 11, 작업요청: 12,
  입고예정일: 13, 박스규격: 14, 박스번호: 16, 파렛트번호: 17,
  실측규격: 18, 주문번호: 19
}

const MAX_COL = 31   // 32번 이후는 읽지 않는다 (창고 내부용)

// 쿠패스가 나중에 오른쪽에 덧붙이기로 한 두 열은 위치를 고정하지 않고 헤더 이름으로 찾는다.
// 창고 내부 열(32~)이 이미 오른쪽에 있어서 몇 번 칸에 올지 우리가 정할 수 없고,
// 고정 인덱스로 박아두면 자리가 어긋났을 때 조용히 빈 값을 읽는다(더 위험하다).
//   입고ID  = 쿠팡 입고요청 shipmentId. 직납은 중국에서 미리 만들어 보내고, 화주향은 비운다
//   BL번호  = House B/L. Unipass 통관 추적의 열쇠
// 두 열이 없으면 빈 값으로 두고 지금과 똑같이 돈다.
const EXTRA_HEADERS = {
  입고ID: '입고id',
  blNo: 'bl번호'
}

// 헤더 비교용 정규화 — 공백과 '/' 를 지우고 소문자로. '입고 ID'·'B/L번호' 도 같이 걸린다.
function headerKey(v) {
  return cellText(v).replace(/[\s/]/g, '').toLowerCase()
}

// 헤더 전체(MAX_COL 밖 포함)를 훑어 두 열의 위치를 찾는다. 없으면 -1.
export function findExtraCols(header) {
  const found = { 입고ID: -1, blNo: -1 }
  const h = Array.isArray(header) ? header : []
  for (let c = 0; c < h.length; c++) {
    const k = headerKey(h[c])
    if (!k) continue
    if (found.입고ID < 0 && k === EXTRA_HEADERS.입고ID) found.입고ID = c
    else if (found.blNo < 0 && k === EXTRA_HEADERS.blNo) found.blNo = c
  }
  return found
}

// 입고ID = 쿠팡 shipmentId(숫자). 창고가 메모를 적어넣는 칸이 여럿이라 값도 검증한다.
function parseShipmentId(v) {
  const s = cellText(v).replace(/\s/g, '')
  return /^\d{4,}$/.test(s) ? s : ''
}

// B/L 번호 = 영숫자(+하이픈). 'GSPC26039E803A3' 같은 형태다.
function parseBlNo(v) {
  const s = cellText(v).replace(/\s/g, '').toUpperCase()
  return /^[A-Z0-9-]{6,}$/.test(s) ? s : ''
}

// 셀 → 화면에 보이는 그대로의 문자열
function cellText(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\r/g, '').trim()
}

// 비교/그룹핑용 키 (공백·줄바꿈 전부 제거)
function cellKey(v) {
  return cellText(v).replace(/\s+/g, '')
}

// "파버네스트\nFabernest" / "Fabernest" 두 형식이 모두 온다 → 마지막 줄이 companyid
function toCompanyid(v) {
  const lines = cellText(v).split('\n').map(s => s.trim()).filter(Boolean)
  return lines.length ? lines[lines.length - 1] : ''
}

// 바코드 칸에 "에그홀더박스제작결제대행" 같은 한글/중국어 설명이 오는 행이 있다(결제대행 건).
// 그런 행은 수량이 구조적으로 안 들어오므로 매칭 대상에서 제외한다.
function isRealBarcode(s) {
  if (!s) return false
  if (/[가-힣ㄱ-ㅎㅏ-ㅣ㐀-鿿]/.test(s)) return false
  return /^[A-Za-z0-9._-]{6,}$/.test(s)
}

// "2026/8/27\n경기광주5(19)" → { 입고예정일: '2026-08-27', 입고센터: '경기광주5(19)' }
// 재고리스트처럼 날짜가 아닌 메모("3.12签收")면 날짜를 비우고 원문을 메모로 남긴다.
function parseEddCell(v) {
  const raw = cellText(v)
  if (!raw) return { 입고예정일: '', 입고센터: '', 입고예정일원문: '' }
  const lines = raw.split('\n').map(s => s.trim()).filter(Boolean)
  const m = lines[0] && lines[0].match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/)
  if (!m) return { 입고예정일: '', 입고센터: '', 입고예정일원문: raw }
  const iso = m[1] + '-' + String(m[2]).padStart(2, '0') + '-' + String(m[3]).padStart(2, '0')
  return { 입고예정일: iso, 입고센터: lines[1] || '', 입고예정일원문: raw }
}

// 수량 칸 해석. 숫자면 대표행, "/" 면 합포장 구성품행.
// 합포장 = 구매요청 단계에서 여러 행(색상별·부자재)을 하나의 완제품으로 묶은 것.
// 완제품 수량은 대표행에만 적히고 나머지 구성품행은 "/" 로 표시된다.
function parseQty(v) {
  if (typeof v === 'number' && isFinite(v)) return { kind: 'qty', 수량: Math.trunc(v) }
  const s = cellText(v)
  if (s === '/' || s === '／') return { kind: 'bundle', 수량: 0 }
  if (s === '') return { kind: 'empty', 수량: 0 }
  if (/^\d+$/.test(s)) return { kind: 'qty', 수량: parseInt(s, 10) }
  return { kind: 'unknown', 수량: 0, 원문: s }
}

// 헤더에서 양식 종류를 판별한다 (10번 컬럼 이름)
export function detectMode(header) {
  const h = cellKey(header && header[COL.수량])
  if (h.indexOf('출고') >= 0) return 'export'
  if (h.indexOf('재고') >= 0) return 'stock'
  return ''
}

/**
 * 창고 엑셀(sheet_to_json header:1 결과)을 정규화한다.
 * @param {Array<Array>} aoa - 0행이 헤더인 2차원 배열
 * @returns {{mode, rows, groups, issues}}
 */
export function parseWarehouseList(aoa) {
  if (!Array.isArray(aoa) || aoa.length < 2) {
    return { mode: '', rows: [], groups: [], issues: [{ line: 0, reason: '빈 파일입니다' }] }
  }
  const header = aoa[0] || []
  const mode = detectMode(header)
  const extra = findExtraCols(header)
  const issues = []
  if (!mode) {
    issues.push({ line: 1, reason: '10번 칸이 출고수량/재고수량이 아닙니다. 창고 양식이 맞는지 확인해 주세요' })
    return { mode: '', rows: [], groups: [], issues }
  }

  const rows = []
  for (let i = 1; i < aoa.length; i++) {
    const raw = aoa[i] || []
    const line = i + 1                       // 엑셀 행번호(1-based, 헤더 포함)
    const cut = raw.slice(0, MAX_COL + 1)    // 32번 이후 무시

    const 바코드 = cellKey(cut[COL.바코드])
    const companyid = toCompanyid(cut[COL.업체])
    const 박스번호 = cellText(cut[COL.박스번호])
    const qty = parseQty(cut[COL.수량])
    const edd = parseEddCell(cut[COL.입고예정일])

    // 완전 빈 행은 조용히 건너뛴다 (엑셀 하단 여백)
    if (!바코드 && !companyid && !박스번호) continue

    const row = {
      line,
      companyid,
      바코드,
      상품명: cellText(cut[COL.상품명]),
      옵션: cellText(cut[COL.옵션]),
      주문번호: cellKey(cut[COL.주문번호]),
      송장번호: cellKey(cut[COL.송장번호]),
      주문날짜: cellText(cut[COL.주문날짜]),
      주문수량: parseQty(cut[COL.주문수량]).수량,
      수량: qty.수량,
      수량종류: qty.kind,
      박스번호,
      박스번호키: cellKey(cut[COL.박스번호]),
      파렛트번호: cellText(cut[COL.파렛트번호]),
      박스규격: cellText(cut[COL.박스규격]),
      실측규격: cellText(cut[COL.실측규격]),
      창고메모: cellText(cut[COL.창고메모]),
      입고예정일: edd.입고예정일,
      입고센터: edd.입고센터,
      입고예정일원문: edd.입고예정일원문,
      // cut 이 아니라 raw 에서 읽는다 — 이 두 열은 창고 내부 열(32~) 오른쪽에 올 수 있다
      입고ID: extra.입고ID >= 0 ? parseShipmentId(raw[extra.입고ID]) : '',
      blNo: extra.blNo >= 0 ? parseBlNo(raw[extra.blNo]) : ''
    }

    if (!companyid) issues.push({ line, reason: '업체 칸이 비어 있습니다', row })
    else if (!isRealBarcode(바코드)) issues.push({ line, reason: '바코드가 아닙니다(결제대행 등): ' + (cellText(cut[COL.바코드]) || '빈칸'), row })
    else if (qty.kind === 'unknown') issues.push({ line, reason: '수량을 읽을 수 없습니다: ' + qty.원문, row })
    else if (!박스번호) issues.push({ line, reason: '박스번호가 비어 있습니다', row })
    else { rows.push(row); continue }
  }

  // 박스 × 바코드(× 1688주문번호, 아래 참고) = 완제품 하나. 그룹 안에 숫자 수량은 정확히 1개, 나머지는 "/" 여야 한다.
  const map = new Map()
  for (const r of rows) {
    const key = r.companyid + '|' + r.박스번호키 + '|' + r.바코드
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(r)
  }

  // 같은 박스·같은 바코드에 수량 행이 여럿이어도, 1688주문번호가 행마다 다르면 주문별로 따로 출고된 것이다
  // (예: 같은 보호필름을 두 번 주문해 한 박스에 담음). 그때는 주문번호로 다시 나눈다.
  // "/" 구성품행은 같은 주문번호의 수량 행에 붙이고, 맞는 주문이 없으면 첫 수량 행에 붙인다
  // (부자재는 다른 주문으로 산 경우가 흔하다).
  //
  // 주문번호까지 같은 수량 행이 여럿이면 두 가지다 — 주문수량(9번 칸)·옵션으로 가른다.
  //   · 둘 다 같다 = 같은 구매요청을 한 박스에 두 번 나눠 담은 것 → 아래에서 수량을 합산한다
  //     (lyqora 335+336 10+10, YZY 10-06 「分两次装进同一个箱子 相加就行」)
  //   · 다르다 = 같은 주문 안의 다른 구매요청(색상 다름, 본주문+추가 1개 등. GUREUMI 737 绿色 5 / 742 橙色 1)
  //     → 합치면 한 문서에 몰리므로 따로 나눈다. 원장 키가 안 겹치게 주문구분에 9번 칸·옵션을 덧붙인다.
  const 합산키 = r => r.주문번호 + '|' + r.주문수량 + '|' + String(r.옵션 || '').replace(/\s+/g, '')
  const entries = []
  for (const [key, list] of map) {
    const 수량행 = list.filter(r => r.수량종류 === 'qty')
    const 나눌수있음 = 수량행.length > 1 && 수량행.every(r => r.주문번호) && new Set(수량행.map(합산키)).size > 1
    if (!나눌수있음) { entries.push([key, list]); continue }
    const sub = new Map()
    수량행.forEach(r => {
      const k = 합산키(r)
      if (!sub.has(k)) sub.set(k, [])
      sub.get(k).push(r)
    })
    const 첫묶음 = ord => Array.from(sub.values()).find(l => l[0].주문번호 === ord)
    list.forEach(r => {
      if (r.수량종류 === 'qty') return
      const 대상 = 첫묶음(r.주문번호) || sub.get(합산키(수량행[0]))
      대상.push(r)
    })
    for (const l of sub.values()) {
      const ord = l[0].주문번호
      // 주문번호만으로 갈리면 지금까지처럼 주문번호만 쓴다(#1110 과 같은 키)
      const 주문구분 = 수량행.filter(r => r.주문번호 === ord).every(r => 합산키(r) === 합산키(l[0])) ? ord : 합산키(l[0])
      entries.push([key + '|' + 주문구분, l.sort((a, b) => a.line - b.line), 주문구분])
    }
  }

  const groups = []
  const 수량없음 = []   // 전부 "/" 인 묶음 — 같은 박스의 다른 상품과 합포장된 것일 수 있다(아래에서 붙인다)
  for (const [key, list, 주문구분] of entries) {
    const 대표들 = list.filter(r => r.수량종류 === 'qty')
    const head = 대표들[0] || list[0]
    if (대표들.length === 0) {
      if (list.every(r => r.수량종류 === 'bundle')) { 수량없음.push(list); continue }
      issues.push({ line: head.line, reason: '박스 ' + head.박스번호.replace(/\n/g, '/') + ' 에 수량이 적힌 행이 없습니다', row: head })
      continue
    }
    // 같은 박스·바코드·1688주문번호·주문수량·옵션의 수량 행이 여럿 = 한 박스에 두 번 나눠 담은 것 → 수량을 합친다.
    // 주문번호가 빈 행이 섞였으면 무엇을 합칠지 모르므로 지금처럼 확인 대상으로 남긴다.
    if (대표들.length > 1 && !대표들.every(r => r.주문번호 && 합산키(r) === 합산키(head))) {
      issues.push({ line: head.line, reason: '박스 ' + head.박스번호.replace(/\n/g, '/') + ' 에 수량 행이 ' + 대표들.length + '개입니다(1개여야 함)', row: head })
      continue
    }
    groups.push({
      key,
      companyid: head.companyid,
      바코드: head.바코드,
      상품명: head.상품명,
      박스번호: head.박스번호,
      파렛트번호: head.파렛트번호,
      입고예정일: head.입고예정일,
      입고센터: head.입고센터,
      수량: 대표들.reduce((합, r) => 합 + r.수량, 0),
      // 합산한 수량 행들의 원래 행 번호(합산이 없으면 빈 배열) — 미리보기·출고 이력에 근거로 남긴다
      합산행: 대표들.length > 1 ? 대표들.map(r => r.line) : [],
      // 주문수량(9번 칸) = 그 발주로 주문한 **전체** 수량. 출고를 나눠 해도 바뀌지 않는다
      // (2026-08-30 창고 확인). 우리 DB 의 구매요청수량과 대조하는 데 쓴다 —
      // 두 값이 다르면 매입가의 분모가 틀렸다는 뜻이라 미리보기에서 알려준다.
      주문수량: head.주문수량,
      // 박스 하나 = 선적 하나. 대표행에 있는 게 정상이지만, 합포장이면 창고가 구성품행에만
      // 적어놓을 수도 있어 그룹 안에서 처음 채워진 값을 쓴다.
      입고ID: (list.find(r => r.입고ID) || {}).입고ID || '',
      blNo: (list.find(r => r.blNo) || {}).blNo || '',
      // 주문번호로 나눈 그룹만 값이 있다 — 출고 원장 멱등키에 덧붙여 같은 박스·바코드·수량끼리 안 겹치게 한다
      주문구분: 주문구분 || '',
      대표행: head,
      // 합산된 나머지 수량 행은 구성품이 아니다 — 대표행 하나로 보낸다(같은 구매요청을 두 번 건드리지 않게)
      구성품행: list.filter(r => r.수량종류 !== 'qty'),
      행: list.filter(r => r === head || r.수량종류 !== 'qty')
    })
  }

  // 바코드가 다른데 수량이 "/" 뿐인 행 = 같은 박스의 다른 상품에 합포장된 부자재다
  // (challantem NOBARCODE 실리콘캡 → 같은 박스의 식기건조대). 같은 화주·같은 박스에 수량 있는
  // 묶음이 딱 하나면 그 묶음의 구성품으로 붙이고 합포장추정 표시를 한다. 실제로 맞는지는
  // 서버가 그 구매요청의 기타요청사항으로 확인한다. 박스에 상대가 없거나 여럿이면 지금처럼 못 읽은 행으로 둔다.
  for (const list of 수량없음) {
    const head = list[0]
    const 상대 = groups.filter(g => g.companyid === head.companyid && g.대표행.박스번호키 === head.박스번호키)
    if (상대.length !== 1) {
      issues.push({ line: head.line, reason: '박스 ' + head.박스번호.replace(/\n/g, '/') + ' 에 수량이 적힌 행이 없습니다', row: head })
      continue
    }
    list.forEach(r => { r.합포장추정 = true })
    상대[0].구성품행 = 상대[0].구성품행.concat(list)
    상대[0].행 = 상대[0].행.concat(list)
  }

  return { mode, rows, groups, issues, extra }
}

// 대표행에 짝지어진 부자재 줄 중 출고수량이 「/」·공백·0 인 줄의 행 번호 집합 — 출고자료 파일에서 이 줄은 뺀다
// (YZY 2026-10-09: 합포장 부자재 「Silicone cap」은 따로 나가지 않는다 / 「也去掉」 공백·0 도 같이).
// 합포장 단가 합산(invoicePack.js)은 groups[].구성품행 을 그대로 쓰므로 이 함수와 무관하다.
// 보호: 짝 없는 행(issues)·대표행 수량이 0/빈 그룹·짝지어지지 않은 정상 행은 포함하지 않는다.
export function bundleLinesInGroups(parsed) {
  const out = new Set()
  const byLine = new Map(((parsed && parsed.rows) || []).map(r => [r.line, r]))
  for (const g of (parsed && parsed.groups) || []) {
    const head = g.대표행
    if (!head || head.수량종류 !== 'qty' || !(head.수량 > 0)) continue
    for (const r of g.구성품행 || []) if (r !== head && (r.수량종류 === 'bundle' || r.수량종류 === 'empty')) out.add(r.line)
    for (const ln of g.합산행 || []) { const r = byLine.get(ln); if (r && r !== head && r.수량종류 === 'qty' && r.수량 === 0) out.add(r.line) }
  }
  return out
}

export default { COL, detectMode, findExtraCols, parseWarehouseList, bundleLinesInGroups }
